import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { CardStack } from "@shared/models/card-stack"
import type { CardStackVersion, DocumentVersion } from "@shared/models/document_version"
import type EmberStore from "cc-frontend/services/ember-store"
import type Rpc from "cc-frontend/services/rpc"
import { cloneDeep, first, isNil, reverse } from "lodash-es"
import { tracked, TrackedArray } from "tracked-built-ins"
import { cached } from "tracked-toolbox"

import * as R from "remeda"; // tree-shaking supported!

interface Signature {
  Args: {
    cardStack: CardStack
    onClose: () => void
  }
}

export default class BrowseLessonHistory extends Component<Signature> {
  @service("ember-store") declare store: EmberStore

  @service declare rpc: Rpc

  // selectedVersionNumber = null

  // selectedVersionId: Ember.computed('state.selectedVersionId', 'lessonVersionSummaries.@each.id', function(){
  //   return get(this, 'state.selectedVersionId') ||
  //          get(_.last(_.sortBy(get(this, 'lessonVersionSummaries'), v => v.attributes.version)), 'id')
  // }),

  @tracked _userSelectedVersion = null
  @tracked versions: null | Array<DocumentVersion> = null

  _isFetchingDocuments = false

  @cached
  get documentVersions() {
    if (this.args.cardStack.id === undefined) return null
    if (this._isFetchingDocuments === true) return this.versions
    if (this.versions) return this.versions
    this._isFetchingDocuments = true
    this.rpc
      .GET_CARD_STACK_VERSIONS(this.args.cardStack.id)
      .then((response) => {
        // @ts-ignore tracked array isn't well typed yet. Once it's typed, this should work
        this.versions = new TrackedArray(reverse(response.data))
      })
      .finally(() => {
        this._isFetchingDocuments = false
      })
    return this.versions
  }

  get groupedVersions() {
    if (this.versions === null) return
    return R.pipe(
      this.versions,
      R.groupBy((version) => {
        let date = dateFns.parse(version.attributes.insertedAt)
        return dateFns.format(date, "YYYY-MM-DD")
      }),
      R.toPairs,
      R.map(([date, summaries]) => ({ date, summaries }))
    )
  }

  get selectedVersionNumber() {
    if (this._userSelectedVersion) return this._userSelectedVersion
    if (this.documentVersions === null) return null
    let mostRecentVersion = first(this.documentVersions)
    return mostRecentVersion?.attributes.version
  }

  @tracked _selectedDocument: null | CardStackVersion = null

  @cached
  get selectedDocument() {
    if (this.selectedVersionNumber === null) return
    if (
      this._selectedDocument &&
      this.selectedVersionNumber === this._selectedDocument.attributes.version
    )
      return this._selectedDocument
    this.store
      .find(
        "card-stack-version",
        `card-stack:${this.args.cardStack.id}:${this.selectedVersionNumber}`
      )
      .then((doc: CardStackVersion) => {
        this._selectedDocument = doc
      })

    return this._selectedDocument
  }

  get cardStackVersion() {
    if (isNil(this.selectedDocument)) return null
    let originalAttributes = this.selectedDocument.attributes.originalDocument
    let cardStack = cloneDeep(this.args.cardStack)
    // @ts-ignore it's saying CardStackAttributes has nothing in common with CardStackAttributes
    cardStack.attributes = originalAttributes
    return cardStack
  }

  @action selectVersion(versionId) {
    console.log("selectVersion", versionId)
    this._userSelectedVersion = versionId
  }

  @action
  rollbackLesson(version) {
    this.rpc.ROLLBACK_CARD_STACK({
      cardStackId: this.args.cardStack.id,
      version: version,
    })
    this.args.onClose()
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Modals::BrowseLessonHistory": typeof BrowseLessonHistory
  }
}
